import * as React from 'react';
import styled, { css } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { PrimaryButton, NegativeButton, PositiveButton, SecondaryButton, WarningButton } from '../../button';
import { IPopupMessageProps } from '../popup-message-props';
import { IBaseComponentProps } from '../../types';

interface IPopupMessageFooterProps extends
    Pick<IPopupMessageProps, 'positiveActionButtonLabel' | 'negativeActionButtonLabel' | 'hideNegativeActionButton' | 'type'>,
    IBaseComponentProps<HTMLDivElement> {
    onActionClick(actionResult: boolean): void;
}

const buttonBaseStyles = css`
    min-width: 100px;
`;

const StyledSecondaryButton = styled(SecondaryButton)`
    ${buttonBaseStyles}
`;

const ButtonTypeMappings = {
    error: styled(NegativeButton)`${buttonBaseStyles}`,
    success: styled(PositiveButton)`${buttonBaseStyles}`,
    warning: styled(WarningButton)`${buttonBaseStyles}`,
    default: styled(PrimaryButton)`${buttonBaseStyles}`
};

const getActionButtonByType = memoizeFunction((type: IPopupMessageProps['type'] = 'information') => {
    switch (type) {
        case 'error':
            return ButtonTypeMappings.error;
        case 'success':
            return ButtonTypeMappings.success;
        case 'warning':
            return ButtonTypeMappings.warning;
        default:
            return ButtonTypeMappings.default;
    }
});

function FooterBase(props: IPopupMessageFooterProps) {
    const {
        positiveActionButtonLabel = 'OK', negativeActionButtonLabel = 'Cancel',
        onActionClick, hideNegativeActionButton, type
    } = props;

    const onPositiveActionClick = React.useCallback(() => onActionClick(true), [onActionClick]);
    const onNegativeActionClick = React.useCallback(() => onActionClick(false), [onActionClick]);
    const ActionButton = React.useMemo(() => getActionButtonByType(type), [type]);

    return (
        <>
            <StyledSecondaryButton hidden={hideNegativeActionButton} label={negativeActionButtonLabel} onClick={onNegativeActionClick} />
            <ActionButton label={positiveActionButtonLabel} onClick={onPositiveActionClick} />
        </>
    );
}

export const PopupMessageFooter = styled(FooterBase).attrs(() => ({
    className: String('ap-confirm-popup-footer')
}))`
    width: 100%;
`;
